import {
  NO_PROGRAM_VALUE,
  PROGRAM_COURSE_SEPARATOR,
  VALUE_SEPARATOR
} from "../../constants"
import { ProgramCourseData } from "../../../../pages/SnapCourseConfiguration/types"
import { IndentationDropdownOptions } from "../../../../../../mp-shared-components/generic-shared-components/components/IndentationDropdown"
import { AutocompleteChangeReason, FilterOptionsState } from "@mui/material"
import { Option } from "../../../../../../mp-shared-components/generic-shared-components/components/Autocomplete/Autocomplete"

export const NO_PROGRAM_OPTION: IndentationDropdownOptions = {
  value: NO_PROGRAM_VALUE,
  label: NO_PROGRAM_VALUE,
  level: 0
}

export const getProgramCoursesOptions = (
  programCourses: ProgramCourseData[],
  metaData?: {
    selectableProgram?: boolean
    hasNoProgramOption?: boolean
  }
): IndentationDropdownOptions[] => {
  const { selectableProgram = false, hasNoProgramOption = false } =
    metaData || {}

  if (!!selectableProgram) {
    return [
      ...(hasNoProgramOption ? [NO_PROGRAM_OPTION] : []),
      ...programCourses.reduce(
        (acc, { program, courses, programDisplayName }) => {
          return [
            ...acc,
            {
              value: program,
              label: programDisplayName || program,
              level: 0,
              optionDetails: {
                courses,
                programDisplayName,
                optionType: "program"
              }
            },
            ...courses.map(course => ({
              value: [program, course].join(VALUE_SEPARATOR),
              label: course,
              level: 1,
              optionDetails: {
                program,
                programDisplayName,
                optionType: "course"
              }
            }))
          ]
        },
        []
      )
    ]
  }

  return programCourses.reduce(
    (acc, { program, courses, programDisplayName }) => {
      return [
        ...acc,
        ...courses.map(course => ({
          value: [program, course].join(VALUE_SEPARATOR),
          label: course,
          optionDetails: { program, programDisplayName, optionType: "course" }
        }))
      ]
    },
    []
  )
}

export const getChipLabel = (
  option: Option,
  programs: { label: string; value: string }[],
  chipLabelSeparator?: string
) => {
  const program = option.value?.split(VALUE_SEPARATOR)?.[0]
  const programDisplayName = programs?.find(el => el.value === program)?.label

  const chipLabel = option.value.replace(
    VALUE_SEPARATOR,
    chipLabelSeparator ?? PROGRAM_COURSE_SEPARATOR
  )

  if (!programDisplayName) return chipLabel

  return chipLabel.replace(program, programDisplayName)
}

export const groupBy =
  (groups: { label: string; value: string }[]) => (option: Option) =>
    groups?.find(group => option.value.startsWith(group.value))?.label ?? ""

export const getMarker =
  (groups: string[], selectableProgram?: boolean) =>
  (option: IndentationDropdownOptions) =>
    selectableProgram ? (!!option.level ? "-" : "") : groups?.length ? "-" : ""

export const formatInputValue = (currentOption: Option) => {
  const currentLabel = currentOption?.value?.replace(
    VALUE_SEPARATOR,
    PROGRAM_COURSE_SEPARATOR
  )

  return currentLabel
}

export const filterProgramCoursesOptions = (
  options: IndentationDropdownOptions[],
  { inputValue }: FilterOptionsState<IndentationDropdownOptions>
) => {
  const inputValueLowerCase = inputValue.toLowerCase().trim()

  return options.filter(
    ({ value, optionDetails }) =>
      // Value can consist of `program` or `program_course`.
      // That's why in this check we split string
      // and try to find match of input value in program or course value
      value
        ?.toLowerCase()
        .split("_")
        .find(item => item.includes(inputValueLowerCase)) ||
      // Program can have own display name, and user need to have possibility to search by display name.
      // That's why we also need to check matching input value with program display name
      optionDetails?.programDisplayName
        ?.toLowerCase()
        .includes(inputValueLowerCase) ||
      // Each program can have courses and if input value matching with course name of this program,
      // then we need to show not only course, but program too
      optionDetails?.courses?.find((course: string) =>
        course?.toLowerCase().includes(inputValueLowerCase)
      )
  )
}

export const getProgramCourseMapping = (
  values: string[]
): Record<string, string[]> => {
  if (!values?.length) return {}

  // Create object from `test` filter array values to Record<`program`, `subject`[]>
  return values.reduce((res, value) => {
    const [program, subject] = value.split("_")

    return {
      ...res,
      [program]: subject
        ? [...(res[program] ?? []), subject]
        : res[program] ?? []
    }
  }, {})
}

export const selectAllCoursesOfPrograms = (
  selectedOption: IndentationDropdownOptions,
  options: IndentationDropdownOptions[],
  selectedOptions: string[],
  reason: AutocompleteChangeReason,
  onChange: (value: string[]) => void
) => {
  let selectedProgramCourseMapping = getProgramCourseMapping(selectedOptions)

  if (selectedOption.optionDetails.optionType === "program") {
    if (reason === "selectOption") {
      selectedProgramCourseMapping = {
        ...selectedProgramCourseMapping,
        [selectedOption.value]: selectedOption.optionDetails.courses
      }
    }

    if (reason === "removeOption") {
      selectedProgramCourseMapping = Object.fromEntries(
        Object.entries(selectedProgramCourseMapping).filter(
          ([program]) => program !== selectedOption.value
        )
      )
    }
  }

  onChange(
    Object.entries(selectedProgramCourseMapping).reduce(
      (res, [program, courses]) => {
        const allCoursesOfProgram = options.find(
          option => option.value === program
        )?.optionDetails.courses

        return [
          ...res,
          ...(allCoursesOfProgram?.length === courses.length ? [program] : []),
          ...courses.map(course => `${program}_${course}`)
        ]
      },
      []
    )
  )
}
